/* What was actually taken off the table, day by day.
 *
 * Realised only. Not a single figure here moves because a price moved: a
 * position up forty percent contributes nothing until some of it is sold, and a
 * position down forty percent contributes nothing until it is. That is the
 * whole point of the view — the value chart already answers "what is it worth",
 * and answers it in a way that swings with the market every day. This answers
 * the other question, which is what the trading itself produced.
 *
 * Two sources, and they are different kinds of record:
 *
 *   - Share sales carry a `realizedGain` in the account's own currency. A sale
 *     with no gain recorded is counted apart rather than as a zero — the same
 *     treatment the tax figures give it, and for the same reason: a sale you
 *     did not write a gain against is not a sale that broke even.
 *   - Contract closes carry a result in USDT, dated per close. Partial closes
 *     are separate events, so a contract taken off in three goes across a
 *     fortnight lands on the three days it happened rather than on the day the
 *     last of it went.
 *
 * Currencies are not mixed. Everything is reported in USD, and a sale in a
 * currency with no rate is skipped and counted rather than converted at
 * something else — the same rule the tax conversion follows. */

import type { AccountRecord, FxRates, Perp, Trade } from '../types';
import { toUsd } from './derive';

export interface RealisedDay {
  /** ISO date, YYYY-MM-DD. */
  date: string;
  /** USD realised that day, from both sources. */
  usd: number;
  fromShares: number;
  fromContracts: number;
  /** How many events made it up, for a screen that wants to say. */
  events: number;
}

export interface RealisedSummary {
  days: RealisedDay[];
  totalUsd: number;
  fromSharesUsd: number;
  fromContractsUsd: number;
  bestDay: RealisedDay | null;
  worstDay: RealisedDay | null;
  greenDays: number;
  redDays: number;
  /** Sales with no gain on record. Counted, never treated as zero. */
  salesWithoutGain: number;
  /** Events skipped because no rate was known for their currency. */
  skippedForRate: number;
}

const EMPTY: RealisedSummary = {
  days: [], totalUsd: 0, fromSharesUsd: 0, fromContractsUsd: 0,
  bestDay: null, worstDay: null, greenDays: 0, redDays: 0,
  salesWithoutGain: 0, skippedForRate: 0,
};

export interface RealisedInput {
  trades: Trade[];
  perps: Perp[];
  accounts: AccountRecord[];
  fx: FxRates | undefined;
  /** Only days on or after this, when given. */
  from?: string;
}

export function realisedByDay(input: RealisedInput): RealisedSummary {
  const { trades, perps, accounts, fx } = input;
  const byId = new Map(accounts.map((a) => [a.id, a]));
  const days = new Map<string, RealisedDay>();

  let salesWithoutGain = 0;
  let skippedForRate = 0;

  const add = (date: string, usd: number, source: 'shares' | 'contracts') => {
    if (input.from && date < input.from) return;
    const row = days.get(date) ?? { date, usd: 0, fromShares: 0, fromContracts: 0, events: 0 };
    row.usd += usd;
    if (source === 'shares') row.fromShares += usd;
    else row.fromContracts += usd;
    row.events += 1;
    days.set(date, row);
  };

  for (const t of trades) {
    if (t.side !== 'sell') continue;
    if (typeof t.realizedGain !== 'number') { salesWithoutGain += 1; continue; }
    const currency = byId.get(t.account)?.currency ?? 'USD';
    const usd = toUsd(t.realizedGain, currency, fx);
    if (usd === null) { skippedForRate += 1; continue; }
    add(t.at.slice(0, 10), usd, 'shares');
  }

  for (const p of perps) {
    /* The dated events when they exist. A contract closed by a version that
       kept only the running total has no dates to place, so it is reported on
       the day it finally closed rather than being dropped — one day is wrong
       for a position taken off in parts, and silence is wrong for all of it. */
    if (p.closes?.length) {
      for (const c of p.closes) add(c.at.slice(0, 10), c.resultUsdt, 'contracts');
    } else if (p.closedAt && typeof p.closedResultUsdt === 'number') {
      add(p.closedAt.slice(0, 10), p.closedResultUsdt, 'contracts');
    }
  }

  const rows = [...days.values()].sort((a, b) => a.date.localeCompare(b.date));
  if (rows.length === 0) return { ...EMPTY, salesWithoutGain, skippedForRate };

  const byResult = [...rows].sort((a, b) => b.usd - a.usd);

  return {
    days: rows,
    totalUsd: rows.reduce((s, d) => s + d.usd, 0),
    fromSharesUsd: rows.reduce((s, d) => s + d.fromShares, 0),
    fromContractsUsd: rows.reduce((s, d) => s + d.fromContracts, 0),
    bestDay: byResult[0] ?? null,
    worstDay: byResult[byResult.length - 1] ?? null,
    greenDays: rows.filter((d) => d.usd > 0).length,
    redDays: rows.filter((d) => d.usd < 0).length,
    salesWithoutGain,
    skippedForRate,
  };
}
